import React, { createContext, useContext, useEffect, useState } from "react";
import getAuthCookieDomain from "../../Utils/getAuthCookieDomain.js";
import { loadObjectFromStorage, saveObjectToStorage } from "../../Utils/StorageUtils.js";
import { useUserPreferences } from "../Data/UserPreferencesProvider.jsx";
import { useHttp } from "../Common/HttpProvider.jsx";
import { WATCHED_EVENTS } from "../Data/EventsProvider.jsx";
import { IS_COMMON_APP, companiesToCheckToken } from "../../constants.js";
import { ThemeContext } from "../../MyThemeProvider.jsx";
import { useLocale } from "../i18n/I18nProvider.jsx";
import { firebaseToken } from "../../Utils/firebase.js";

/** Устанавливает куку auth с JWT-токеном, чтобы браузер отправлял её и на видеосервис. */
function setAuthCookie(token) {
  if (!token) return;

  const encoded = encodeURIComponent(token);
  let cookie = `auth=${encoded}; Path=/`;

  const domain = getAuthCookieDomain();
  if (domain) {
    cookie += `; Domain=${domain}`;
  }

  // Для HTTPS-доменов включаем Secure и SameSite=None,
  // чтобы кука отправлялась и при переходе/редиректе на видеосервис.
  if (window.location.protocol === "https:") {
    cookie += "; Secure; SameSite=None";
  } else {
    cookie += "; SameSite=Lax";
  }

  document.cookie = cookie;
}

/** Очищает куку auth (используется при выходе пользователя). */
function clearAuthCookie() {
  const domain = getAuthCookieDomain();
  let cookie = "auth=; Path=/; Max-Age=0";

  if (domain) {
    cookie += `; Domain=${domain}`;
  }

  if (window.location.protocol === "https:") {
    cookie += "; Secure; SameSite=None";
  } else {
    cookie += "; SameSite=Lax";
  }

  document.cookie = cookie;
}

const CHECKING_RESULT = "checking-result";

/**
 * @callback FnRegistration
 * @param {String} name
 * @param {String} phone
 * @param {String} aboutMe
 * @param {String} password
 * @param {Number} subjectId
 * @param {Number} offerId
 * @returns {Promise<void>}
 */

/**
 * @callback SendUserSessions
 * @param {SessionsType[]} sessions
 * @param {boolean} [silent] Если true - ответ с поздравлениями о завершении миссии игнорируется
 * @param {string} [authToken] Альтернативный авторизационный токен. На тот случай, когда запрос выполняется до сохранения токена в хранилище
 * @returns {Promise<void>}
 */

/**
 * @typedef {Object} AuthProviderContext
 * @property {?LoginResponse} user
 * @property {?Company} company
 * @property {?Company} company2
 * @property {?LoginResponse} subject
 * @property {?CheckEmailResponse} checkEmailResponse
 * @property {FnStringVoid} checkEmail
 * @property {FnStringNumberVoid} login
 * @property {FnStringNumberVoid} loginByKey
 * @property {FnRegistration} register
 * @property {FnAsyncObject} resetPassword
 * @property {Function} signOut
 * @property {Function} setSubject
 * @property {Function} setCompany2
 * @property {Function} setUser
 * @property {SendUserSessions} sendUserSessions
 */

/**
 * @type {React.Context<AuthProviderContext>}
 */
const authContext = createContext({
  user: null,
  company: null,
  company2: null,
  subject: null,
  checkEmailResponse: null,
  rewardsData: null,
  /** @type {FnStringVoid} */ checkEmail: () => {},
  /** @type {FnStringNumberVoid} */ login: () => {},
  /** @type {FnStringStringVoid} */ loginWithGoogle: () => {},
  /** @type {FnStringNumberVoid} */ loginByKey: () => {},
  /** @type {FnRegistration} */ register: () => {},
  /** @type {FnAsyncObject} */ resetPassword: () => {},
  /** @type {function} */ resetRewardsData: () => {},
  signOut: () => {},
  setSubject: () => {},
  setCompany2: () => {},
  setUser: () => {},
  sendUserSessions: () => {},
});

/**
 * @returns {AuthProviderContext}
 */
export const useAuth = () => useContext(authContext);

/**
 * @param {React.ReactNode} children
 */
const AuthProvider = ({ children }) => {
  const { clear } = useUserPreferences();
  const { signOutRef, post, authTokenRef, get, patch } = useHttp();
  const { resetTheme, resetFont } = useContext(ThemeContext);
  const { setCompanyLocales } = useLocale();

  const [authEmail, setAuthEmail] = useState(/** @type {?String} */ null);
  const [company2, _setCompany2] = useState(/** @type {?Company} */ loadObjectFromStorage("company2"));
  const [rewardsData, setRewardsData] = useState(/** @type {?Rewards} */ null);

  const storageDataVersion = localStorage.getItem("version");

  if (!storageDataVersion) {
    localStorage.setItem("version", "1");
  }

  if (storageDataVersion && storageDataVersion != "1") {
    localStorage.removeItem("user");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("company");
    localStorage.removeItem("company2");
    localStorage.removeItem("color");
    localStorage.removeItem("subject");
    localStorage.removeItem("subjects");
    localStorage.removeItem(CHECKING_RESULT);
    localStorage.removeItem("current_branch");
    localStorage.removeItem("current_level");
    localStorage.setItem("version", "1");

    if (company2?.urn) {
      window.location.href = `/${company2.urn}`;
    } else {
      window.location.href = "/";
    }
  }

  const [user, setUser] = useState(/** @type LoginResponse */ loadObjectFromStorage("user"));
  const [company, setCompany] = useState(/** @type Company */ loadObjectFromStorage("company"));
  const [subject, setSubject] = useState(/** @type ?LoginResponse */ company ? loadObjectFromStorage("subject") : null);
  const [checkEmailResponse, setCheckEmailResponse] = useState(
    /** @type {?CheckEmailResponse} */ loadObjectFromStorage(CHECKING_RESULT),
  );

  // initialize saved in local storage auth token
  const subjectToken = subject && subject.token;
  authTokenRef.current = subjectToken || (user && user.token);

  // Если при инициализации уже есть токен, синхронизируем его в куку для видеосервиса
  if (authTokenRef.current) {
    setAuthCookie(authTokenRef.current);
  }

  // load saved authEmail
  useEffect(() => {
    setAuthEmail(checkEmailResponse && checkEmailResponse.userEmail);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // учет сессий пользователя
  useEffect(() => {
    /** */
    const sessionLogData = () => {
      if (user && subject) {
        if (document.visibilityState === "hidden") {
          const sessions = loadObjectFromStorage("sessions") || [];
          const endDate = new Date().getTime();
          const start = loadObjectFromStorage("startSession");
          const newSessions = [...sessions, { start: start || endDate, end: endDate }];
          saveObjectToStorage("sessions", newSessions);
        } else {
          const start = new Date().getTime();
          saveObjectToStorage("startSession", start);
        }
      }
    };
    document.addEventListener("visibilitychange", sessionLogData);
    return () => {
      document.removeEventListener("visibilitychange", sessionLogData);
    };
  }, [user, subject]);

  // Обновление набора языков компании
  useEffect(() => {
    if (company) {
      setCompanyLocales(company.localeSet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  // Обновление набора языков компании по company2
  useEffect(() => {
    if (company2) {
      setCompanyLocales(company2.localeSet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company2]);

  // clear states
  useEffect(() => {
    if (!user) {
      localStorage.removeItem("userInfo");
    }
    if (!company) {
      localStorage.removeItem("subject");
      setSubject(null);
    }
  }, [user, company]);

  // Periodically check auth token
  useEffect(() => {
    if (!subjectToken) return;

    const intervalHandle = setInterval(() => {
      checkAuthTokenIfNeed();
    }, 10000);

    return () => clearInterval(intervalHandle);
  }, [subjectToken]);

  /** */
  const checkAuthTokenIfNeed = () => {
    if (company2 && !companiesToCheckToken.includes(company2.id)) return;

    get("userInfo/check", null, {}, true).then();
  };

  /**
   * @param {?Company} data
   */
  const setCompany2 = (data) => {
    _setCompany2(data);
    setCompany(data);
    if (!data) {
      localStorage.removeItem("company2");
    } else {
      saveObjectToStorage("company2", data);
      saveObjectToStorage("company", data);
    }
  };

  /** @type {FnStringVoid} */
  const checkEmail = async (email) => {
    /** @type {CheckEmailResponse} */
    const resp = await post("checkEmail", { email }, null, company2 && { "company-id": company2.id });
    if (resp) {
      saveObjectToStorage("company", resp.company);
      setAuthEmail(email);
      setCompany(resp.company);
      resp.userEmail = email;
      saveObjectToStorage(CHECKING_RESULT, resp);
      setCheckEmailResponse(resp);
    }
  };

  /** @type {FnStringNumberVoid} */
  const login = async (password, subjectId, offerId) => {
    /** @type {LoginResponse} */
    const loginResponse = await post(
      "login",
      { email: authEmail, password, subjectId, offerId },
      null,
      company2 && { "company-id": company2.id },
    );
    if (loginResponse) {
      saveObjectToStorage("user", loginResponse);
      setUser(loginResponse);
      authTokenRef.current = loginResponse.token;
      // При успешном логине сохраняем токен и в куку, чтобы он был доступен видеосервису
      setAuthCookie(loginResponse.token);
    }
  };

  /** @type {FnAsyncStringStringVoid} */
  const loginWithGoogle = async (email, googleIdToken) => {
    /** @type {LoginResponse} */
    const loginResponse = await post(
      "google-login",
      { userEmail: email, googleIdToken },
      null,
      company2 && { "company-id": company2.id },
    );
    if (loginResponse) {
      saveObjectToStorage("user", loginResponse);
      setUser(loginResponse);
      authTokenRef.current = loginResponse.token;
    }
  };

  /** @type {FnStringNumberVoid} */
  const loginByKey = async (loginKey, deckId, eventId) => {
    /***/
    const searchParam = `loginKey=${loginKey}&deckId=${deckId}&eventId=${eventId}`;
    const loginResponse = await get(`login?${searchParam}`, null, {});
    if (loginResponse) {
      saveObjectToStorage("user", loginResponse);
      setUser(loginResponse);
      authTokenRef.current = loginResponse.token;
      // При логине по ключу (loginKey) тоже выставляем куку с токеном
      setAuthCookie(loginResponse.token);
      return loginResponse;
    } else {
      return null;
    }
  };

  /** @type {FnRegistration} */
  const register = async (name, phone, aboutMe, password, subjectId, offerId) => {
    /** @type {LoginResponse} */
    const resp = await post(
      "registration",
      {
        name,
        email: authEmail,
        phone: phone || "",
        aboutMe: aboutMe || null,
        password,
        subjectId,
        offerId,
      },
      null,
      company2 && { "company-id": company2.id },
    );
    if (resp) {
      saveObjectToStorage("user", resp);
      setUser(resp);
      authTokenRef.current = resp.token;
      // После успешной регистрации сразу проставляем куку с токеном
      setAuthCookie(resp.token);
    }
  };

  /** @returns {Promise<?Object>} */
  const resetPassword = async () => {
    return post("passwordReset", { email: authEmail });
  };

  /** @type {SendUserSessions} */
  const sendUserSessions = async (sessions, silent = false, authToken = null) => {
    if (!authTokenRef.current) return;

    const resp = await post("sessions", sessions, null, authToken ? { authorization: authToken } : {});
    if (resp) {
      saveObjectToStorage("sessions", []);
      if (!silent) {
        setRewardsData(resp.rewards);
      }
    }
  };

  /** */
  const signOut = () => {
    if (authTokenRef.current && firebaseToken) {
      patch(`firebaseTokens`, { newFirebaseToken: "", oldFirebaseToken: firebaseToken }).then();
    }
    clear();

    const sessions = loadObjectFromStorage("sessions") || [];
    const endDate = new Date().getTime();
    const start = loadObjectFromStorage("startSession");
    const newSessions = [...sessions, { start, end: endDate }];
    sendUserSessions(newSessions, true);

    localStorage.removeItem("user");
    localStorage.removeItem("company");
    localStorage.removeItem(CHECKING_RESULT);
    localStorage.removeItem(WATCHED_EVENTS);
    localStorage.removeItem("current_branch");
    localStorage.removeItem("current_level");
    setUser(null);
    setCompany(null);
    setAuthEmail(null);
    setCheckEmailResponse(null);
    if (IS_COMMON_APP) {
      setCompany2(null);
    }
    resetTheme();
    resetFont();
    authTokenRef.current = null;
    // При выходе пользователя очищаем куку авторизации, чтобы видеосервис тоже перестал его узнавать
    clearAuthCookie();
  };

  signOutRef.current = signOut;

  /** */
  const resetRewardsData = () => {
    setRewardsData(null);
  };

  /** @type AuthProviderContext */
  const value = {
    user,
    company,
    company2,
    subject,
    checkEmailResponse,
    checkEmail,
    login,
    loginWithGoogle,
    loginByKey,
    rewardsData,
    register,
    resetPassword,
    signOut,
    setSubject,
    setCompany2,
    setUser,
    sendUserSessions,
    resetRewardsData,
  };
  return <authContext.Provider value={value}>{children}</authContext.Provider>;
};

export default AuthProvider;
